import {
  Alert,
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useStyles from "../../../components/common/table/styles";
import {
  calculateTotal,
  calculateTotalValue,
  calculateVatValue,
  quantityFormElements,
} from "./invoiceUntils";
import { invoiceValidateForm } from "./invoiceUntils"; // import validation function
import { useSelector } from "react-redux";
import CustomButton from "../../../components/common/button/CustomButton";
import { AddCircle, RemoveCircle } from "@mui/icons-material";

interface QuantityTableProps {
  initialData?: any[];
  formData?: any;
  setFormData?: any;
  invoiceType?: any;
  isFormReadyOnly?: any;
  isNewInvoice?: boolean; // Optional prop to indicate if it's a new invoice
}

const QuantityTable: React.FC<QuantityTableProps> = ({
  initialData = [],
  formData,
  setFormData,
  invoiceType,
  isFormReadyOnly,
  isNewInvoice,
}) => {

  const { editClientOriginalData } = useSelector(
    (state: any) => state.adminClients,
  );
  const { invoiceDetails } = useSelector((state: any) => state.invoice);
  const [errors, setErrors] = useState<any>({});
  const [alertVATMessaage, setAlertVATMessaage] = useState<any>(null);
  const [allTotals, setAllTotals] = useState({
    totalSum: 0,
    vatValueSum: 0,
    sumOfTotal: 0,
  });

  const classes = useStyles();
  // Ensure formData is initialized properly
  useEffect(() => {
    if (!formData?.items) {
      setFormData((prev: any) => ({
        ...prev,
        items:
          initialData.length > 0
            ? initialData
            : [
                {
                  quantity: "",
                  description: "",
                  rate: "",
                  price: "",
                  total: "",
                  vat_rate: Number(editClientOriginalData?.vat_percent || 0),
                  vat_value: "",
                },
              ],
      }));
    }
  }, [formData, initialData, setFormData, isNewInvoice]);

  const tableHeadings = [
    { label: "Quantity" },
    { label: "Description", align: "left" },
    { label: "Rate (£)", align: "left" },
    { label: "Price (£)", align: "left" },
    { label: "VAT Rate (%)", align: "center" },
    { label: "VAT Value (£)", align: "center" },
    { label: "Total (£)", align: "left" },
    { label: "", align: "center" },
  ];

  const calculateAllTotals = (data: any[]) => {
    if (!data || data.length === 0) {
      return { totalSum: 0, vatValueSum: 0, sumOfTotal: 0, zeroVatSum: 0 };
    }
    return data.reduce(
      (acc, row) => {
        const price = parseFloat(row.price) || 0;
        const vatValue = parseFloat(row.vat_value) || 0;
        const total = parseFloat(row.total) || 0;
        const vatRate = row.vat_rate ? parseFloat(row.vat_rate) : 0;

        return {
          totalSum: acc.totalSum + price,
          vatValueSum: acc.vatValueSum + vatValue,
          sumOfTotal: acc.sumOfTotal + total,
          zeroVatSum:
            vatRate === 0 || row.vat_rate == null
              ? acc.zeroVatSum + price
              : acc.zeroVatSum,
        };
      },
      { totalSum: 0, vatValueSum: 0, sumOfTotal: 0, zeroVatSum: 0 },
    );
  };

  useEffect(() => {
    // ✅ Skip recalculation if invoice type is "final"
    if (!isNewInvoice) return;

    // ✅ Also skip if no items
    if (!formData?.items || formData.items.length === 0) return;

    // Recalculate all item rows
    const updatedItems = formData.items.map((item: any) => {
      const quantity = item.quantity || 0;
      const rate = item.rate || 0;
      const vat_rate = item.vat_rate || 0;

      // Calculate base price (before VAT)
      const basePrice = calculateTotal(quantity, rate);

      // Adjust price based on VAT inclusion
      const price = formData.is_vat_included
        ? (parseFloat(basePrice) * 100) / (100 + parseFloat(vat_rate || 0))
        : basePrice;

      const vat_value = calculateVatValue(String(price), vat_rate);
      const total = calculateTotalValue(String(price), vat_value);

      const numericPrice =
        typeof price === "number" ? price : parseFloat(String(price) || "0");
      const roundedPrice = parseFloat(numericPrice.toFixed(2));

      return {
        ...item,
        price: roundedPrice,
        vat_value,
        total,
      };
    });

    // Recalculate all totals
    const { totalSum, vatValueSum, sumOfTotal, zeroVatSum } =
      calculateAllTotals(updatedItems);
    // Update entire form data once (single setFormData)
    setFormData({
      ...formData,
      items: updatedItems,
      net: formData.is_values_fixed ? formData.net : totalSum,
      vat_amount: formData.is_values_fixed ? formData.vat_amount : vatValueSum,
      total_amount: formData.is_values_fixed
        ? formData.is_vat_included
          ? Number(formData.net) + Number(formData.vat_amount)
          : formData.net
        : sumOfTotal,
      quick_pay: formData.is_values_fixed
        ? formData.is_vat_included
          ? Number(formData.net) + Number(formData.vat_amount)
          : formData.net
        : sumOfTotal,
      items_price: totalSum,
      items_vat: vatValueSum,
      items_total: sumOfTotal,
      zero_vat: zeroVatSum,
    });
  }, [formData?.is_vat_included]);

  const handleInputChange = (
    rowIndex: number,
    fieldId: string,
    value: string,
  ) => {
    const updatedItems = formData?.items ? [...formData.items] : [];
    // Update the field value
    updatedItems[rowIndex] = {
      ...updatedItems[rowIndex],
      [fieldId]: value,
    };

    // Handle VAT rate
    if (fieldId === "vat_rate") {
      // User explicitly changes VAT
      updatedItems[rowIndex].vat_rate = value ?? formData?.vat_percent ?? 0;
    } else if (
      !updatedItems[rowIndex].vat_rate &&
      fieldId === "quantity" &&
      value
    ) {
      // Assign default VAT only when user enters quantity
      updatedItems[rowIndex].vat_rate = formData?.vat_percent ?? 0;
    }

    const quantity = updatedItems[rowIndex].quantity;
    const rate = updatedItems[rowIndex].rate;
    const vat_rate = updatedItems[rowIndex].vat_rate;

    // calculated total price and total vat value
    if (
      fieldId === "quantity" ||
      fieldId === "rate" ||
      fieldId === "vat_rate"
    ) {
      const basePrice = calculateTotal(quantity, rate);
      let price;
      // ⭐ CASE 1: Special Rule for "packing material"
      if (
        !formData?.is_vat_included &&
        String(updatedItems[rowIndex]?.description)?.toLowerCase() ===
          "packing materials"
      ) {
        price = parseFloat(updatedItems[rowIndex]?.price); // price = rate * qty
      }

      // CASE 1 → Now NEW behaviour:
      // Final Invoice + VAT included = VAT must be extracted from TOTAL VALUE (user given)
      else if (
        formData?.is_vat_included &&
        formData?.invoice_type === "final"
      ) {
        const totalValue = updatedItems[rowIndex].total || basePrice;

        // reverse VAT on total (total_amount * 100 / (100 + vat_rate))
        price =
          (parseFloat(totalValue) * 100) / (100 + parseFloat(vat_rate || 0));
      }
      // CASE 2 → OLD behaviour:
      else if (
        formData?.is_vat_included &&
        formData?.invoice_type !== "final"
      ) {
        // price formula: basePrice * 100 / (100 + vat_rate)
        price =
          (parseFloat(basePrice) * 100) / (100 + parseFloat(vat_rate || 0));
      } else {
        price = parseFloat(basePrice);
      }
      const vatValue = calculateVatValue(String(price), vat_rate);
      updatedItems[rowIndex].price = price.toFixed(2);
      updatedItems[rowIndex].vat_value = vatValue;
      updatedItems[rowIndex].vat_rate = vat_rate;
      updatedItems[rowIndex].total = calculateTotalValue(
        String(price),
        vatValue,
      );
    }

    // if (rowIndex === formData.items.length - 1 && value) {
    //   updatedItems.push({
    //     quantity: "",
    //     description: "",
    //     rate: "",
    //     price: "",
    //     total: "",
    //     vat_rate: "",
    //     vat_value: "",
    //   });
    // }

    const {
      totalSum = 0,
      vatValueSum = 0,
      sumOfTotal = 0,
      zeroVatSum = 0,
    } = calculateAllTotals(updatedItems);

    setFormData({
      ...formData,
      items: updatedItems,
      net: formData?.is_values_fixed ? formData?.net : totalSum,
      vat_amount: formData?.is_values_fixed
        ? formData?.vat_amount
        : vatValueSum,
      total_amount: formData?.is_values_fixed
        ? formData?.is_vat_included
          ? Number(formData?.net) + Number(formData?.vat_amount)
          : formData?.net
        : formData?.is_vat_included
          ? sumOfTotal
          : sumOfTotal,
      quick_pay: formData?.is_values_fixed
        ? formData?.is_vat_included
          ? Number(formData?.net) + Number(formData?.vat_amount)
          : formData?.net
        : formData?.is_vat_included
          ? sumOfTotal
          : sumOfTotal,
      items_price: totalSum,
      items_vat: vatValueSum,
      items_total: sumOfTotal,
      zero_vat: zeroVatSum,
    });

    // Trigger validation whenever input changes
    const { isError, localError } = invoiceValidateForm({
      items: updatedItems,
    });
    setErrors(localError);
  };

  useEffect(() => {
    if (formData?.items) {
      setAllTotals(calculateAllTotals(formData.items));
    }
  }, [formData]);

  const validateVatRatesInTable = (items: any[]) => {
    const allowedRates = ["0", "0.00", "5", "5.00", "20", "20.00"];
    const invalidRow = items.some(
      (item) =>
        item?.vat_rate !== undefined &&
        item?.vat_rate !== null &&
        item?.vat_rate !== "" &&
        !allowedRates.includes(String(item.vat_rate)),
    );

    if (invalidRow) {
      setAlertVATMessaage(
        "Please adjust your VAT Rates as QuickBooks only accepts 0, 5 or 20%",
      );
    } else {
      setAlertVATMessaage(null);
    }
  };

  useEffect(() => {
    if (formData?.items) {
      validateVatRatesInTable(formData.items);
    }
  }, [formData?.items]);

  const removeRow = (rowIndex: number) => {
    const updatedItems = formData.items.filter(
      (_: any, i: number) => i !== rowIndex,
    );

    // Recalculate totals after removing a row
    const {
      totalSum = 0,
      vatValueSum = 0,
      sumOfTotal = 0,
      zeroVatSum = 0,
    } = calculateAllTotals(updatedItems);

    setFormData({
      ...formData,
      items: updatedItems,
      net: formData?.is_values_fixed ? formData?.net : totalSum,
      vat_amount: formData?.is_values_fixed
        ? formData?.vat_amount
        : vatValueSum,
      total_amount: formData?.is_values_fixed
        ? formData?.is_vat_included
          ? formData?.net + formData?.vat_amount
          : formData?.net
        : formData?.is_vat_included
          ? sumOfTotal
          : sumOfTotal,
      quick_pay: formData?.is_values_fixed
        ? formData?.is_vat_included
          ? formData?.net + formData?.vat_amount
          : formData?.net
        : formData?.is_vat_included
          ? sumOfTotal
          : sumOfTotal,
      items_price: totalSum,
      items_vat: vatValueSum,
      items_total: sumOfTotal,
      zero_vat: zeroVatSum,
    });
  };

  const isTableDisabled = invoiceDetails?.status === "PaidRemoval";

  console.log("errors-10",errors)

  return (
    <Grid container sx={{ mb: 2, mt: 1 }}>
      <Grid item md={12} xs={12}>
        {alertVATMessaage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {alertVATMessaage}
          </Alert>
        )}
        <TableContainer component={Paper} sx={{ overflow: "visible" }}>
          <Table sx={{ overflow: "none" }}>
            <TableHead sx={{ background: "#02906F" }}>
              <TableRow>
                {tableHeadings.map((item, index) => (
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      padding: "4px",
                      color: "#fff",
                      textAlign: "center",
                    }}
                    className={classes.tableHeadCell}
                    key={index}
                  >
                    {item.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {formData?.items?.map((row: any, rowIndex: number) => (
                <TableRow
                  key={rowIndex}
                  sx={{
                    ...(isTableDisabled &&
                      row?.item_id && {
                        pointerEvents: "none",
                        opacity: 0.6,
                        backgroundColor: "#eeecef",
                      }),
                  }}
                >
                  {quantityFormElements.map((field) => (
                    <TableCell
                      key={field.id}
                      sx={{
                        width: field.id === "description" ? "auto" : "100px",
                        padding: "10px",
                      }}
                    >
                      <TextField
                        fullWidth={field.id === "description"}
                        onPaste={(e: any) =>
                          field?.id === "description" ? "" : e.preventDefault()
                        }
                        onKeyDown={(event: any) => {
                          if (field?.id === "description") return;
                          if (!field?.patternAllowed) return;

                          if (
                            event.ctrlKey ||
                            event.metaKey ||
                            [
                              "Backspace",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                              "ArrowUp",
                              "ArrowDown",
                              "Delete",
                              "Home",
                              "End",
                              "Enter",
                            ].includes(event.key)
                          ) {
                            return;
                          }

                          const target = event.target as HTMLInputElement;
                          const selectionStart =
                            target.selectionStart ?? target.value.length;
                          const selectionEnd =
                            target.selectionEnd ?? target.value.length;
                          const nextValue =
                            target.value.slice(0, selectionStart) +
                            event.key +
                            target.value.slice(selectionEnd);

                          if (!field.patternAllowed.test(nextValue)) {
                            event.preventDefault();
                          }
                        }}
                        value={
                          row[field.id] || row[field.id] === 0
                            ? row[field.id]
                            : ""
                        }
                        disabled={
                          field.id === "vat_rate"
                            ? false // always editable
                            : isNewInvoice ||
                                isFormReadyOnly ||
                                invoiceType !== "final"
                              ? false
                              : true
                        }
                        onChange={(e) =>
                          handleInputChange(rowIndex, field.id, e.target.value)
                        }
                        onBlur={() => {
                          if (field.id === "vat_rate") {
                            validateVatRatesInTable(formData?.items || []);
                          }
                        }}
                        variant="outlined"
                        error={
                          errors?.items?.[rowIndex]?.[field.id] ? true : false
                        }
                        // helperText={errors?.items?.[rowIndex]?.[field.id] || ""}
                        InputProps={{
                          ...(field.inputProps && field.inputProps),
                          ...{ disabled: isFormReadyOnly || field?.readonly },
                          sx:
                            field.type === "text"
                              ? {
                                  height: "30px",
                                  "&.Mui-disabled": {
                                    backgroundColor: "#eeecef",
                                  },
                                }
                              : { padding: "10px 4px", height: "36px" },
                        }}
                      />
                    </TableCell>
                  ))}
                  {/* Delete row button */}
                  <TableCell
                    sx={{
                      textAlign: "left",
                      padding: "0px !important",
                      width: "26px !important",
                      minWidth: "26px !important",
                      maxWidth: "26px !important",
                      //      opacity: 0.6,
                      // backgroundColor: "#eeecef",
                    }}
                  >
                    {formData?.items?.length > 1 &&
                      (isNewInvoice ||
                        formData?.category === null ||
                        (formData?.category === "final" &&
                          (formData?.is_copy || formData?.is_duplicate))) && 
                          (
                        <IconButton
                          color="error"
                          onClick={() => removeRow(rowIndex)}
                          sx={{ marginLeft: "-2px" }}
                        >
                          <RemoveCircle sx={{ fontSize: 15 }} />
                        </IconButton>
                      )}
                  </TableCell>
                </TableRow>
              ))}

              <TableRow sx={{ backgroundColor: "#eeecef" }}>
                <TableCell
                  sx={{
                    textAlign: "center",
                    padding: "0px",
                    width: "20px",
                  }}
                >
                  {invoiceType !== "final" && (
                    <IconButton color="primary">
                      <CustomButton
                        variant="outlined"
                        style={{
                          background: "#fff",
                          padding: "4px 10px",
                          display: "flex",
                          alignItems: "center",
                        }}
                        disabled={
                          (!isNewInvoice &&
                            invoiceDetails?.category === "final" &&
                            !invoiceDetails?.is_copy &&
                            !invoiceDetails?.is_duplicate) ||
                          isTableDisabled
                            ? true
                            : false
                        }
                        title={
                          <>
                            <AddCircle
                              sx={{
                                width: "14px",
                                height: "14px",
                                marginRight: "6px",
                              }}
                              color="primary"
                            />{" "}
                            Add Row
                          </>
                        }
                        onClick={() => {
                          const updatedItems = formData?.items
                            ? [...formData.items]
                            : [];
                          updatedItems.push({
                            quantity: "",
                            description: "",
                            rate: "",
                            price: "",
                            total: "",
                            vat_rate: Number(
                              editClientOriginalData?.vat_percent || 0,
                            ),
                            vat_value: "",
                          });
                          setFormData({
                            ...formData,
                            items: updatedItems,
                          });
                        }}
                      />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell colSpan={2}></TableCell>
                <TableCell sx={{ textAlign: "center", padding: "10px" }}>
                  <TextField
                    disabled
                    variant="outlined"
                    InputProps={{ sx: { height: "30px" } }}
                    value={allTotals.totalSum.toFixed(2)}
                  />
                </TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ textAlign: "center", padding: "10px" }}>
                  <TextField
                    disabled
                    variant="outlined"
                    InputProps={{ sx: { height: "30px" } }}
                    value={allTotals.vatValueSum.toFixed(2)}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: "center", padding: "10px" }}>
                  <TextField
                    disabled
                    variant="outlined"
                    InputProps={{ sx: { height: "30px" } }}
                    value={allTotals.sumOfTotal.toFixed(2)}
                  />
                </TableCell>
                <TableCell
                  sx={{ textAlign: "center", padding: "0px" }}
                ></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default QuantityTable;
